import { memo, useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ChevronDown, Search, X } from 'lucide-react';

const SEARCHABLE_THRESHOLD = 50;
const MAX_RENDERED = 100;

function SearchableSelectBase({
  options = [],
  value,
  onChange,
  placeholder = 'Select',
  className = '',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const normalized = useMemo(
    () =>
      options.map((opt) =>
        typeof opt === 'object' ? opt : { label: opt, value: opt },
      ),
    [options],
  );

  const selectedLabel = useMemo(() => {
    const found = normalized.find((o) => o.value === value);
    return found ? found.label : '';
  }, [normalized, value]);

  const filtered = useMemo(() => {
    if (!query.trim()) return normalized.slice(0, MAX_RENDERED);
    const q = query.toLowerCase();
    const results = [];
    for (const opt of normalized) {
      if (opt.label.toLowerCase().includes(q)) {
        results.push(opt);
        if (results.length >= MAX_RENDERED) break;
      }
    }
    return results;
  }, [normalized, query]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
      setQuery('');
    }
  }, [open]);

  const handleSelect = useCallback(
    (val) => {
      if (disabled) return;
      onChange(val);
      setOpen(false);
      setQuery('');
    },
    [disabled, onChange],
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      if (disabled) return;
      onChange('');
    },
    [disabled, onChange],
  );

  return (
    <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`w-full h-[42px] px-4 flex items-center justify-between text-[#000000]
            bg-[#ffffff] border rounded-md truncate focus:bg-[#000000]/10
            focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
        >
          <span
            className={`truncate text-sm ${!selectedLabel ? 'text-[#000000]/70' : ''}`}
          >
            {selectedLabel || placeholder}
          </span>
          <div className="flex items-center gap-1 shrink-0 ml-2">
            {selectedLabel && (
              <X
                className="h-3.5 w-3.5 opacity-50 hover:opacity-100"
                onClick={handleClear}
              />
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 rounded-md border bg-[#ffffff] shadow-lg"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
        align="start"
        sideOffset={4}
      >
        <div className="flex items-center px-3 py-2 border-b bg-white rounded-t-md">
          <Search className="h-4 w-4 text-[#000000]/50 shrink-0 mr-2" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${placeholder.replace('Select ', '')}...`}
            className="flex-1 text-sm text-[#000000] bg-transparent outline-none placeholder:text-[#000000]/40"
          />
          {query && (
            <X
              className="h-3.5 w-3.5 text-[#000000]/40 cursor-pointer hover:text-[#000000]"
              onClick={() => setQuery('')}
            />
          )}
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: 288 }}>
          {filtered.length === 0 ? (
            <p className="text-sm text-[#000000]/50 text-center py-4">
              No results found
            </p>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleSelect(opt.value)}
                className={`w-full text-left px-3 py-2 text-sm cursor-pointer text-[#000000]
                  hover:bg-[#000000]/15 transition-colors ${opt.value === value ? 'bg-[#000000]/20 font-medium' : ''}`}
              >
                {opt.label}
              </button>
            ))
          )}
          {query === '' && normalized.length > MAX_RENDERED && (
            <p className="text-xs text-[#000000]/40 text-center py-2 border-t">
              Type to search all {normalized.length.toLocaleString()} options
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const SearchableSelect = memo(SearchableSelectBase);

function StandardSelectBase({
  options = [],
  value,
  onChange,
  placeholder = 'Select',
  className = '',
  maxHeight = '300px',
  disabled = false,
}) {
  const { optionsWithKeys, indexToValue } = useMemo(() => {
    const isObjectOptions =
      options.length > 0 &&
      typeof options[0] === 'object' &&
      'label' in options[0] &&
      'value' in options[0];

    const normalized = isObjectOptions
      ? options
      : options.map((opt) => ({ label: String(opt), value: String(opt) }));

    const map = new Map();
    const list = normalized.map((opt, index) => {
      const key = `opt::${index}`;
      map.set(key, opt.value);
      return { label: opt.label, value: opt.value, key };
    });

    return { optionsWithKeys: list, indexToValue: map };
  }, [options]);

  const { internalValue, displayLabel } = useMemo(() => {
    const found = optionsWithKeys.find((o) => o.value === value);
    return {
      internalValue: found ? found.key : '',
      displayLabel: found ? found.label : placeholder,
    };
  }, [optionsWithKeys, value, placeholder]);

  const handleValueChange = useCallback(
    (key) => {
      const original = indexToValue.get(key);
      if (original !== undefined && original !== value) onChange(original);
    },
    [indexToValue, value, onChange],
  );

  return (
    <Select
      value={internalValue}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger
        className={`w-full h-[42px] px-4 text-[#000000] data-[placeholder]:text-[#000000]/70
          bg-[#ffffff] border rounded-md truncate
          focus:bg-[#ffffff]/90 focus:outline-none focus:ring-0 focus:ring-offset-0
          disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      >
        <SelectValue placeholder={placeholder}>{displayLabel}</SelectValue>
      </SelectTrigger>
      <SelectContent className="rounded-md border bg-[#ffffff] shadow-lg">
        <div className="overflow-y-auto" style={{ maxHeight }}>
          {optionsWithKeys.map((option) => (
            <SelectItem
              key={option.key}
              value={option.key}
              className="text-[#000000] focus:bg-[#000000]/15 focus:text-[#000000] cursor-pointer"
            >
              {option.label}
            </SelectItem>
          ))}
        </div>
      </SelectContent>
    </Select>
  );
}

const StandardSelect = memo(StandardSelectBase);

function CustomSelectBase({
  options = [],
  value,
  onChange,
  placeholder = 'Select',
  required = false,
  className = '',
  maxHeight = '300px',
  disabled = false,
}) {
  const isLarge = options.length > SEARCHABLE_THRESHOLD;

  return (
    <div className="relative w-full">
      {isLarge ? (
        <SearchableSelect
          options={options}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
        />
      ) : (
        <StandardSelect
          options={options}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
          maxHeight={maxHeight}
          disabled={disabled}
        />
      )}

      {required && (
        <input
          type="text"
          required
          value={value || ''}
          onChange={() => {}}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export const CustomSelect = memo(CustomSelectBase);
